<template lang="pug">
    .booking.full-height-error.not-valid(v-if="!enabledExtensions.includes('event-manager')")
        Error(:message="null" :code="403")
    .event.full-height-error.not-allowed(v-else-if="pathNotValid")
        Error(:message="$t('URL Not Valid')")
    .event.full-height-error(v-else-if="error")
        template(v-if="error.code")
            Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
        template(v-else)
            Error(:message="$t('Something went wrong!')" :code="501")
    .booking(v-else)
        tabs(:items="tabs" :active-tab="$route.fullPath" @click="activateTab")
            template(slot="tabbar-buttons")
                // el-tooltip(class="item" effect="dark" :content="$t('Delete')" placement="bottom")
                //     el-button.delete-button(circle type="danger" icon="el-icon-fa-trash-alt far")
                // el-tooltip(class="item" effect="dark" :content="$t('Dismiss')" placement="bottom")
                //     el-button(circle icon="el-icon-fa-times fas")
                // el-tooltip(class="item" effect="dark" :content="$t('Save and close')" placement="bottom")
                //     el-button(circle type="primary" icon="el-icon-fa-check fas")
                // el-tooltip(class="item" effect="dark" :content="$t('Save')" placement="bottom")
                //     el-button(circle type="success" icon="el-icon-fa-save fas")
                el-button.delete-button(
                    circle v-if="! loading && isDeletable"
                    type="danger"
                    icon="el-icon-fa-trash-alt far"
                    :loading="deleting"
                    :disabled="isDisabledSubmit && ! deleting"
                    @click="deleteCurrentBooking"
                )
                el-button(
                    circle icon="el-icon-fa-times fas"
                    :disabled="isDisabledSubmit"
                    @click="leave"
                )
                el-button(
                    circle v-if="! loading"
                    type="primary"
                    icon="el-icon-fa-check fas"
                    :disabled="isDisabledSubmit && ! savingAndClose"
                    :loading="savingAndClose"
                    @click="submit($event, listRoute)"
                )
                el-button(
                    circle v-if="! loading"
                    type="success"
                    icon="el-icon-fa-save fas"
                    :disabled="isDisabledSubmit && ! saving"
                    :loading="saving"
                    @click="submit"
                )

        el-form(label-position="top" :model="form" :rules="rules" ref="form")
            all-input-fields
            el-card(shadow="always" v-loading="loading")
                router-view.booking-form(:key="$route.path" :class="{'loading-panel': loading}")
            .form-buttons
                el-button(
                    round v-if="! loading"
                    icon="el-icon-fa-times fas"
                    :disabled="isDisabledSubmit"
                    @click="leave"
                ) {{ $t('Dismiss') }}
                el-button(
                    round v-if="! loading"
                    type="primary"
                    icon="el-icon-fa-check fas"
                    :loading="savingAndClose"
                    :disabled="isDisabledSubmit && ! savingAndClose"
                    @click="submit($event, listRoute)"
                ) {{ $t('Save and close') }}
                el-button(
                    round v-if="! loading"
                    type="success"
                    icon="el-icon-fa-save fas"
                    :loading="saving"
                    :disabled="isDisabledSubmit && ! saving"
                    @click="submit"
                ) {{ $t('Save') }}
</template>

<i18n>
{
    "en": {
        "\"{field}\" is required.": "\"{field}\" is required."
    },
    "de": {
        "\"{field}\" is required.": "„{field}“ ist ein Pflichtfeld.",
        "Dependencies could not be fetched.": "Abhängigkeiten konnten nicht abgerufen werden.",
        "Booking could not be fetched.": "Anmeldung konnte nicht gefunden werden.",
        "Booking could not be deleted.": "Anmeldung konnte nicht gelöscht werden.",
        "Booking deleted successfully!": "Anmeldung erfolgreich gelöscht!",
        "Booking could not be saved.": "Anmeldung konnte nicht gespeichert werden.",
        "Booking saved successfully!": "Anmeldung erfolgreich gespeichert!",
        "Booking Not Found": "Anmeldung nicht gefunden",
        "Booking": "Anmeldung",
        "Duplicate booking": "E-Mail bereits angemeldet",
        "This email address is already registered for this slot.": "Diese E-Mail-Adresse ist für diesen Zeitslot bereits angemeldet."
    }
}
</i18n>

<script>
import _ from 'lodash';
import { useSetupStore, useEventManagerBookingStore } from '@/stores';

import router from '@/router';

import { findMissingRequiredAnswer } from '@/extensions/eventManager/views/Partials/RegistrationFieldInputs';

import Tabs from '@/components/Tabs';
import Error from '@/components/Error';

// validate all fields, regardless if the corresponding component is mounted or not
import AllInputFields from './AllInputFields';

export default {
    name: 'Booking',
    data() {
        return {
            listRoute: { name: 'event-booking-list' },
            error: false,
            pathNotValid: false,
            loading: false,
            saving: false,
            savingAndClose: false,
            deleting: false
        };
    },
    components: {
        Tabs,
        Error,
        AllInputFields
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true);
            if (reload) {
                this.setInitializedFlag(false);
                this.initialize();
            }
        }
    },
    computed: {
        id() {
            return _.get(this.form, 'id', false);
        },
        isDeletable() {
            return !!this.id;
        },
        isDisabledSubmit() {
            return this.saving || this.savingAndClose || this.deleting;
        },
        tabs() {
            const id = this.$route.params.id;

            return [
                {
                    name: this.$t('Booking'),
                    path: `/event-manager/booking/${id}/main`,
                    icon: {
                        name: 'calendar-check',
                        group: 'fas'
                    }
                }
            ];
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions;
        },
        setupStore() {
            return useSetupStore();
        },
        bookingStore() {
            return useEventManagerBookingStore();
        },
        form() {
            return this.bookingStore.form;
        },
        options() {
            return this.bookingStore.options;
        },
        initialized() {
            return this.bookingStore.formInitialized;
        },
        validateOnEnter() {
            return this.bookingStore.formValidateOnEnter;
        },
        rules() {
            return this.bookingStore.rules;
        }
    },
    methods: {
        initialize() {
            if (!this.enabledExtensions.includes('event-manager')) {
                this.setInitializedFlag(true);
                return;
            }

            if (this.initialized !== true) {
                this.setInitializedFlag(true);

                const fetchDependencies = () => Promise.all([this.fetchEventOptions(this.activeCms.id)]);

                this.error = false;
                this.loading = true;

                const handleDependencyError = err => {
                    const { graphQLErrors } = err

                    let code = 501;
                    let message = 'An error occurred!';

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code;
                        message = _.get(graphQLErrors, '0.message') || message;
                    }

                    let notify = true;
                    switch (code) {
                        case 401:
                        case 403:
                            notify = false;
                            this.error = { code, message: 'Dependencies could not be fetched.' };
                            break;
                        // case 404:
                        //     break;
                        default:
                            this.error = true;
                            break;
                    }

                    if (notify) {
                        this.$notify.error({
                            title: this.$t(message),
                            message: this.$t('Dependencies could not be fetched.'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    }

                    this.loading = false;

                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);
                }

                if (this.$route.params.id === 'new') {
                    this.bookingStore.reset();
                    // this.bookingStore.resetRules()

                    // fetchDependencies().then(() => this.bookingStore.updateRules(rules))
                    fetchDependencies()
                        .then(this.initializeBooking)
                        .catch(handleDependencyError)
                        .finally(() => {
                            this.loading = false;
                        });
                } else if (!isNaN(this.$route.params.id)) {
                    this.fetchBooking(this.$route.params.id)
                        .then(() => {
                            // this.bookingStore.resetUI()

                            this.loading = false;

                            // fetch options for dropdowns
                            fetchDependencies()
                                .then(() => {
                                    this.bookingStore.resetRules();
                                    // this.bookingStore.resetUI()
                                })
                                .catch(handleDependencyError);
                        })
                        .catch(err => {
                            const { graphQLErrors } = err

                            let code = 501;
                            let message = 'An error occurred!';

                            if (graphQLErrors) {
                                code = _.get(graphQLErrors, '0.code') || code;
                                message = _.get(graphQLErrors, '0.message') || message;
                            } else {
                                this.error = true;
                            }

                            switch (code) {
                                case 401:
                                    this.error = { code, message: 'You are not authorized' };
                                    break;
                                case 403:
                                    this.error = { code, message: 'You are not allowed to be here' };
                                    break;
                                case 404:
                                    this.error = { code, message: 'Booking Not Found' };
                                    break;
                                default:
                                    this.$notify.error({
                                        title: this.$t('An error occurred!'),
                                        message: this.$t('Booking could not be fetched.'),
                                        iconClass: 'fas el-icon-fa-times',
                                        position: 'bottom-left',
                                        customClass: 'dark',
                                        duration: 8000
                                    });
                                    break;
                            }

                            this.loading = false;

                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);
                        });
                } else {
                    this.loading = true;
                    this.pathNotValid = true;
                }
            } else {
                if (this.validateOnEnter === true) {
                    setTimeout(() => {
                        this.$refs.form.validate(() => {
                            // do nothing
                            // callback function is required :(
                        });
                    }, 100);
                }
            }
        },
        deleteCurrentBooking() {
            this.deleting = true;
            this.bookingStore.deleteBooking(this.id)
                .then(() => {
                    // this.deleting = false

                    router.push(this.listRoute);

                    this.$notify.success({
                        title: this.$t('Booking deleted successfully!'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })
                .catch(err => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);

                    this.deleting = false;

                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Booking could not be deleted.'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                });
        },
        submit(event, redirect = false) {
            this.$refs.form.validate((valid, errors) => {
                if (valid) {
                    // the custom questions of the registration form are not part of the rules
                    const missing = findMissingRequiredAnswer(this.bookingStore.template.fields, this.bookingStore.answers);
                    if (missing) {
                        this.$notify.warning({
                            title: this.$t('Form is not valid!'),
                            message: this.$t('"{field}" is required.', { field: missing }),
                            iconClass: 'fas el-icon-fa-exclamation-circle',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                        return;
                    }

                    if (redirect) this.savingAndClose = true;
                    else this.saving = true;

                    let save = this.create;
                    if (this.id) save = this.update;

                    save(this.form)
                        .then(booking => {
                            this.$notify.success({
                                title: this.$t('Booking saved successfully!'),
                                iconClass: 'fas el-icon-fa-check',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });

                            if (redirect) {
                                router.push(redirect);
                            } else {
                                if (this.$route.params.id === 'new') {
                                    const route = router.currentRoute
                                    route.params.id = booking.id
                                    router.replace(route)
                                }
                            }
                        })
                        .catch(err => {
                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);

                            const graphqlMessage = _.get(err, 'graphQLErrors[0].message', '');
                            const isDuplicate = graphqlMessage.toLowerCase().includes('already been booked with the same email');

                            this.$notify.error({
                                title: this.$t(isDuplicate ? 'Duplicate booking' : 'An error occurred!'),
                                message: this.$t(isDuplicate ? 'This email address is already registered for this slot.' : 'Booking could not be saved.'),
                                iconClass: 'fas el-icon-fa-times',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000
                            });
                        })
                        .finally(() => {
                            if (redirect) this.savingAndClose = false;
                            else this.saving = false;
                        });
                } else {
                    this.setValidateOnEnterFlag(true);

                    // check the invalid fields
                    // and go to the corresponding tab
                    // or open the corresponding closed area
                    // TODO: scroll to invalid field

                    let invalidTabs = [];
                    _.each(errors, (error, path) => {
                        if (_.indexOf(['name'], path) >= 0) {
                            invalidTabs.push(`/event-manager/booking/${this.$route.params.id}/main`);
                        }
                    });

                    invalidTabs = _.uniq(invalidTabs);

                    // check if the current tab
                    // is present in invalidTabs
                    // if not, go to next (first) invalid tab

                    const match = _.indexOf(invalidTabs, this.$route.fullPath);

                    // let switchTab = false;
                    if (match < 0) {
                        invalidTabs.every(invalidTab => {
                            if (invalidTab && this.activeTab !== invalidTab) {
                                const tab = _.find(this.tabs, { path: invalidTab });
                                if (tab) {
                                    this.activateTab(tab);

                                    // break loop after the first tab has been activated
                                    return false;
                                }
                            }
                        });
                    }

                    // // triger the validation again
                    // // so the previously hidden fields will get the errors
                    // // ---
                    // // does not work if the component has been destroyed
                    // // in the meantime (f.a. by switching tabs)
                    // if (switchTab === false) {
                    //     const form = this.$refs.form
                    //     setTimeout(() => {
                    //         form.validate(() => {
                    //             // do nothing
                    //             // callback function is required :(
                    //         })
                    //     }, 1000);
                    // }

                    this.$notify.warning({
                        title: this.$t('Form is not valid!'),
                        message: this.$t('Correct your input and submit again.'),
                        iconClass: 'fas el-icon-fa-exclamation-circle',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                }
            });
        },
        leave() {
            router.push(this.listRoute);
        },
        setInitializedFlag(value) {
            this.bookingStore.setFormInitialized(value);
        },
        setValidateOnEnterFlag(value) {
            this.bookingStore.setFormValidateOnEnter(value);
        },
        activateTab(item) {
            return this.setupStore.activateTab(item);
        },
        fetchBooking(id) {
            return this.bookingStore.fetchBooking(id);
        },
        fetchEventOptions(cmsId) {
            return this.bookingStore.fetchEventOptions(cmsId);
        },
        initializeBooking() {
            // This action might not exist in the Pinia store - needs to be a no-op or handled differently
            // The original Vuex action was 'ext_eventManager_booking/initializeBooking'
        },
        create(booking) {
            return this.bookingStore.create(booking);
        },
        update(booking) {
            return this.bookingStore.update(booking);
        }
    },
    mounted() {
        this.initialize();
    },
    // eslint-disable-next-line
    beforeRouteEnter(to, from, next) {
        // let cms = _.get(store, 'state.setup.activeCms');

        // redirect if necessary
        const id = _.get(to, 'params.id') || 'new';

        if (to.name === 'event-booking-form') {
            next({ name: 'event-booking-main', params: { id } });
        } else {
            next();
        }
    },
    beforeRouteLeave(to, from, next) {
        next();

        setTimeout(() => {
            this.bookingStore.reset();
            this.bookingStore.resetUI();
            this.$nextTick(() => this.bookingStore.resetRules());
        }, 300);
    },
    destroyed() {
        // console.log('destroyed');
    }
};
</script>

<style lang="sass" scoped>
.booking::v-deep
    .el-card__body
        padding: 0
        & > section
            padding: 20px
</style>
